import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetStravaDisplayPreferencesQuery,
  PageType,
} from '../../../../types/__generated__';
import { PageMetaContext } from '../../../context/PageMetaContext';
import useListTimelineEntries, {
  fetchPage,
} from '../../../hooks/timeline/useListTimelineEntries';
import useFullPageQuery from '../../../hooks/useFullPageQuery';
import { useNotNullableContext } from '../../../hooks/useNotNullableContext';
import { usePageOwner } from '../../../hooks/usePageOwner';
import { LazyLoader } from '../../LazyLoader/LazyLoader';
import PageSection from '../../PageSection/PageSection';
import { useIsStravaEnabled } from '../../Strava/StravaBehindFeatureFlag';
import getStravaDisplayPreferences from '../StravaTimelineEntry/StravaDisplayPreferences.gql';
import Timeline from '../Timeline/Timeline';
import { TimelineContext } from '../TimelineContext';
import { getMeasurementSystemFromSettings } from '../../../utils/measurementSystem';

const TimelineUpdateEditorLazy = React.lazy(() =>
  import('../TimelineUpdateEditor/TimelineUpdateEditor'),
);

const TimelinePageSection: FunctionComponent = () => {
  const { isPageOwner } = usePageOwner();
  const { t, i18n } = useTranslation('common', { useSuspense: false });
  const { pageShortName, pageType } = useNotNullableContext(PageMetaContext);
  const { data: settingsData } = useFullPageQuery<
    GetStravaDisplayPreferencesQuery
  >(getStravaDisplayPreferences);

  const measurementSystem = getMeasurementSystemFromSettings(
    i18n,
    settingsData?.page?.strava?.settings,
  );

  const { loading, data, fetchMore, refetch } = useListTimelineEntries(
    measurementSystem,
  );
  const isEnabled = useIsStravaEnabled();

  if (loading || !data?.page) {
    return null;
  }

  const { timeline, legacyId } = data.page;

  const pageId = isEnabled ? legacyId : pageShortName;

  const fetchNextPage = fetchPage(
    data.page.timeline?.pageInfo.endCursor ?? '',
    fetchMore,
  );

  // The new timeline service doesn't provide totalCount so counting nodes
  const entryCount = timeline
    ? Math.max(timeline.nodes?.length ?? 0, timeline.totalCount ?? 0)
    : 0;

  return entryCount || isPageOwner ? (
    <PageSection title={t('page.timelineTitle')} hasBorder={true}>
      {isPageOwner && (
        <LazyLoader>
          <TimelineUpdateEditorLazy
            pageId={pageId}
            pageSlug={pageShortName}
            pageType={pageType.toUpperCase() as PageType}
            measurementSystem={measurementSystem}
          />
        </LazyLoader>
      )}
      {entryCount > 0 && (
        <TimelineContext.Provider value={{ refetch, fetchNextPage }}>
          <Timeline
            timeline={timeline ?? undefined}
            owner={data.page.owner ?? undefined}
            measurementSystem={measurementSystem}
          />
        </TimelineContext.Provider>
      )}
    </PageSection>
  ) : null;
};

export default TimelinePageSection;
